/**
 * Utility functions for handling geolocation
 */

/**
 * Storage keys
 */
const LOCATION_PERMISSION_KEY = 'rnr_location_permission';
const LOCATION_PROMPT_SHOWN_KEY = 'rnr_location_prompt_shown';

/**
 * Permission states
 */
export const PERMISSION_STATES = {
  GRANTED: 'granted',
  DENIED: 'denied',
  PROMPT: 'prompt',
  UNKNOWN: 'unknown'
};

/**
 * Get the current geographical location of the user
 * @returns {Promise<{latitude: number, longitude: number} | null>} A promise that resolves with the latitude and longitude or null if permissions denied
 */
export const getCurrentLocation = async () => {
  // First check if permissions are already denied
  const permissionState = await checkLocationPermissionState();
  
  // If permissions are already denied, resolve with null instead of rejecting
  if (permissionState === PERMISSION_STATES.DENIED) {
    console.log('Location permissions already denied, skipping location request');
    return null;
  }
  
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      console.log('Geolocation not supported by browser');
      resolve(null); // Resolve with null instead of rejecting
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        // Save permission state when successful
        saveLocationPermissionState(PERMISSION_STATES.GRANTED);
        resolve({ latitude, longitude });
      },
      (error) => {
        // Update permission state based on error
        if (error.code === error.PERMISSION_DENIED) {
          saveLocationPermissionState(PERMISSION_STATES.DENIED);
          resolve(null); // Resolve with null instead of rejecting
        } else {
          // For other errors (position unavailable, timeout), we can still reject
          reject(error);
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  });
};

/**
 * Check if geolocation is supported by the browser
 * @returns {boolean} True if geolocation is supported
 */
export const isGeolocationSupported = () => {
  return !!navigator.geolocation;
};

/**
 * Request permission to access the user's location
 * This function should be called only in response to a user action (e.g., button click)
 * to prevent the browser's permission dialog from appearing automatically
 * @returns {Promise<string>} A promise that resolves with the permission state
 */
export const requestLocationPermission = () => {
  return new Promise((resolve, reject) => {
    if (!isGeolocationSupported()) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    // Mark that we've shown the prompt
    localStorage.setItem(LOCATION_PROMPT_SHOWN_KEY, 'true');
    
    // Request position explicitly - browser will show native permission dialog
    navigator.geolocation.getCurrentPosition(
      (position) => {
        saveLocationPermissionState(PERMISSION_STATES.GRANTED);
        resolve(PERMISSION_STATES.GRANTED);
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          saveLocationPermissionState(PERMISSION_STATES.DENIED);
          resolve(PERMISSION_STATES.DENIED);
        } else {
          // Timeout or position unavailable
          resolve(PERMISSION_STATES.PROMPT);
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  });
};

/**
 * Check the current permission state without prompting
 * @returns {Promise<string>} A promise that resolves with the permission state
 */
export const checkLocationPermissionState = async () => {
  // Check if we have permissions API
  if (navigator.permissions && navigator.permissions.query) {
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      console.log(result);
      // Still save the state so other functions can check it
      saveLocationPermissionState(result.state);
      return result.state;
    } catch (error) {
      console.warn('Error checking permission state:', error);
      return PERMISSION_STATES.UNKNOWN;
    }
  }
  
  return PERMISSION_STATES.UNKNOWN;
};

/**
 * Save the location permission state to localStorage
 * @param {string} state - The permission state to save
 */
export const saveLocationPermissionState = (state) => {
  localStorage.setItem(LOCATION_PERMISSION_KEY, state);
};

/**
 * Check if the location permission prompt has been shown before
 * @returns {boolean} True if the prompt has been shown before
 */
export const hasLocationPromptBeenShown = () => {
  return localStorage.getItem(LOCATION_PROMPT_SHOWN_KEY) === 'true';
};

/**
 * Reset the location prompt shown flag
 */
export const resetLocationPromptShown = () => {
  localStorage.removeItem(LOCATION_PROMPT_SHOWN_KEY);
};
